/*
 * Компонент AdminSettings — общие настройки приложения и управление тарифами
 *
 * Основные функции:
 * - Загрузка актуальных настроек с сервера при открытии
 * - Редактирование username бота поддержки в Telegram
 * - Управление видимостью главного логотипа приложения
 * - Сохранение изменений через SettingsEndpoint
 * - CRUD-операции над тарифами: создание, редактирование, удаление
 */
<script setup lang="ts">
// Хуки жизненного цикла и реактивность Vue
import { ref, reactive, onMounted, computed } from 'vue'
// Определение размера экрана — для динамического padding
import { useDisplay } from 'vuetify'
// Утилита определения Telegram-контекста
import { isTelegram } from '@/utils/isTelegram'
// Привязка реактивных свойств стора к компоненту
import { storeToRefs } from 'pinia'
// Компонент заголовка секции
import SectionHeader from '@/components/SectionHeader.vue'
// Стор настроек — загрузка и обновление настроек приложения
import { useSettingsStore } from '@/modules/settings/store/settingsStore'
// Стор администрирования — CRUD тарифов
import { useAdminStore } from '@/modules/admin/store/adminStore'
// Стор уведомлений (тосты)
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
// Типизация тарифного плана
import type { ITariff } from '@/modules/api/types/ITariff'
// Типизация payload создания тарифа
import type { ICreateTariffPayload } from '@/modules/admin/types/ICreateTariffPayload'
// Типизация payload обновления тарифа
import type { IUpdateTariffPayload } from '@/modules/admin/types/IUpdateTariffPayload'

// Инициализация стора настроек
const settingsStore = useSettingsStore()
// Инициализация стора уведомлений
const feedbackStore = useFeedbackStore()
// Определение мобильного экрана для динамического padding
const { mobile } = useDisplay()
// Инициализация стора администрирования
const adminStore = useAdminStore()
// Реактивная привязка списка тарифов из стора
const { tariffs } = storeToRefs(adminStore)

// --- Общие настройки ---

// Локальное состояние username бота поддержки в Telegram
const supportBotUsername = ref('')
// Локальное состояние флага отображения главного логотипа
const showMainLogo = ref(true)
// Локальное состояние email поддержки
const supportEmail = ref('')
// Локальное состояние списка URL прокси-серверов
const proxyURLs = ref<string[]>([])

// Вычисляемое свойство для отображения и редактирования прокси-URL через запятую
const proxyURLsText = computed({
  get: () => proxyURLs.value.join(', '),
  set: (val) => {
    proxyURLs.value = val.split(',').map(url => url.trim()).filter(Boolean)
  }
})

/**
 * Сохраняет изменения общих настроек на сервер
 *
 * Основные действия:
 * - Отправляет текущие значения supportBotUsername и showMainLogo
 * - Вызывает settingsStore.updateSettings для обновления через gRPC
 */
const saveChanges = async () => {
  await settingsStore.updateSettings({
    supportBotUsername: supportBotUsername.value,
    showMainLogo: showMainLogo.value,
    supportEmail: supportEmail.value,
    proxyURLs: proxyURLs.value,
  })
}

// --- Управление тарифами ---

// Флаг открытия диалога создания тарифа
const isCreateTariffDialogOpen = ref(false)
// Флаг открытия диалога подтверждения удаления
const isDeleteTariffDialogOpen = ref(false)
// Тариф, выбранный для удаления — нужен для подтверждения с названием и ценой в диалоге
const tariffToDelete = ref<ITariff | null>(null)
// Множество идентификаторов тарифов в режиме редактирования
const editingTariffIds = ref<Set<string>>(new Set())

// Массив id открытых панелей тарифов (v-expansion-panels, multiple)
const expandedTariffIds = ref<string[]>([])

// Снапшоты редактируемых тарифов — для отката по «Отмена». v-model правит объект из стора напрямую,
// без снапшота изменённые поля остаются в сторе даже при отмене редактирования
const editingTariffSnapshots = new Map<string, Partial<ITariff>>()

// Список регулярных тарифов для выбора тарифа автопродления при промо-тарифе
const regularTariffsOptions = computed(() =>
  tariffs.value
    .filter((t) => !t.isPromo)
    .map((t) => ({
      title: `${t.periodName} (${t.price} ₽)`,
      value: t.id,
    }))
)

// Реактивный payload для создания нового тарифа
const newTariff = reactive<ICreateTariffPayload>({
  price: 0,
  periodDays: 30,
  periodName: '',
  isPopular: false,
  referralBonusDays: 0,
  isPromo: false,
  fallbackTariffID: '',
})

// Реактивный объект ошибок валидации формы создания тарифа
const createTariffErrors = reactive({
  price: false,
  periodDays: false,
  periodName: false,
  fallbackTariffID: false,
})

// Ошибки валидации тарифов в режиме редактирования по id
const editingTariffErrors = reactive<
  Record<
    string,
    {
      price?: boolean
      periodDays?: boolean
      periodName?: boolean
      fallbackTariffID?: boolean
    }
  >
>({})

/**
 * Открывает диалог создания тарифа со сброшенными значениями
 */
const openCreateTariffDialog = () => {
  Object.assign(newTariff, {
    price: 0,
    periodDays: 30,
    periodName: '',
    isPopular: false,
    referralBonusDays: 0,
    isPromo: false,
    fallbackTariffID: '',
  })
  createTariffErrors.price = false
  createTariffErrors.periodDays = false
  createTariffErrors.periodName = false
  createTariffErrors.fallbackTariffID = false
  isCreateTariffDialogOpen.value = true
}

/**
 * Создаёт новый тариф через стор и закрывает диалог при успехе
 */
const createTariff = async () => {
  createTariffErrors.price = false
  createTariffErrors.periodDays = false
  createTariffErrors.periodName = false
  createTariffErrors.fallbackTariffID = false

  if (newTariff.price <= 0) {
    createTariffErrors.price = true
    feedbackStore.showToast({
      type: 'error',
      message: 'Цена тарифа должна быть больше 0',
    })
    return
  }

  if (newTariff.periodDays <= 0) {
    createTariffErrors.periodDays = true
    feedbackStore.showToast({
      type: 'error',
      message: 'Количество дней тарифа должно быть больше 0',
    })
    return
  }

  if (!newTariff.periodName?.trim()) {
    createTariffErrors.periodName = true
    feedbackStore.showToast({
      type: 'error',
      message: 'Заполните название периода тарифа',
    })
    return
  }

  if (newTariff.isPromo && !newTariff.fallbackTariffID) {
    createTariffErrors.fallbackTariffID = true
    feedbackStore.showToast({
      type: 'error',
      message: 'Выберите тариф автопродления для промо-тарифа',
    })
    return
  }

  const payload: ICreateTariffPayload = {
    ...newTariff,
    fallbackTariffID: newTariff.isPromo ? newTariff.fallbackTariffID || undefined : undefined,
  }
  const success = await adminStore.createTariff(payload)
  if (success) isCreateTariffDialogOpen.value = false
}

/**
 * Проверяет, находится ли тариф в режиме редактирования
 *
 * @param {string} id - Идентификатор тарифа
 * @returns {boolean} true если тариф редактируется
 */
const isTariffEditing = (id: string) => editingTariffIds.value.has(id)

/**
 * Сохраняет копию редактируемых полей тарифа — чтобы можно было откатить при отмене
 *
 * @param {ITariff} tariff - Объект тарифа, для которого делается снапшот
 */
const saveTariffSnapshot = (tariff: ITariff) => {
  editingTariffSnapshots.set(tariff.id, {
    price: tariff.price,
    periodDays: tariff.periodDays,
    periodName: tariff.periodName,
    referralBonusDays: tariff.referralBonusDays,
    isPopular: tariff.isPopular,
    isPromo: tariff.isPromo,
    fallbackTariffID: tariff.fallbackTariffID,
  })
}

/**
 * Возвращает поля тарифа к снапшоту (если он был сохранён) и очищает снапшот
 *
 * @param {ITariff} tariff - Объект тарифа, поля которого откатываются
 */
const restoreTariffSnapshot = (tariff: ITariff) => {
  const snap = editingTariffSnapshots.get(tariff.id)
  if (!snap) return
  Object.assign(tariff, snap)
  editingTariffSnapshots.delete(tariff.id)
}

/**
 * Переключает режим редактирования тарифа с синхронизацией развёрнутой панели.
 *
 * Основные действия:
 * - Повторный клик по карандашу при открытой панели в edit — откат правок, выход из edit и свёртка.
 * - Иначе — сохранение снапшота, вход в edit; если панель свёрнута — раскрыть.
 *
 * @param {ITariff} tariff - Объект тарифа
 */
const toggleTariffEditMode = (tariff: ITariff) => {
  const isOpen = expandedTariffIds.value.includes(tariff.id)
  const inEdit = editingTariffIds.value.has(tariff.id)
  // Открыта в edit — откатываем несохранённые правки и сворачиваем
  if (isOpen && inEdit) {
    restoreTariffSnapshot(tariff)
    expandedTariffIds.value = expandedTariffIds.value.filter((id) => id !== tariff.id)
    editingTariffIds.value.delete(tariff.id)
    return
  }
  // Переход в edit — сохраняем снапшот для возможного отката
  saveTariffSnapshot(tariff)
  editingTariffIds.value.add(tariff.id)
  if (!isOpen) {
    expandedTariffIds.value = [...expandedTariffIds.value, tariff.id]
  }
}

/**
 * Отмена редактирования — откатывает несохранённые правки, выходит из режима редактирования и сворачивает панель.
 *
 * @param {ITariff} tariff - Объект тарифа
 */
const cancelTariffEdit = (tariff: ITariff) => {
  restoreTariffSnapshot(tariff)
  delete editingTariffErrors[tariff.id]
  editingTariffIds.value.delete(tariff.id)
  expandedTariffIds.value = expandedTariffIds.value.filter((id) => id !== tariff.id)
}

/**
 * Сохраняет изменения тарифа через стор
 *
 * @param {ITariff} tariff - Объект тарифа с обновлёнными полями
 */
const saveTariffChanges = async (tariff: ITariff) => {
  const errors = {
    price: tariff.price <= 0,
    periodDays: tariff.periodDays <= 0,
    periodName: !tariff.periodName?.trim(),
    fallbackTariffID: Boolean(tariff.isPromo && !tariff.fallbackTariffID),
  }
  editingTariffErrors[tariff.id] = errors

  if (errors.price) {
    feedbackStore.showToast({
      type: 'error',
      message: 'Цена тарифа должна быть больше 0',
    })
    return
  }

  if (errors.periodDays) {
    feedbackStore.showToast({
      type: 'error',
      message: 'Количество дней тарифа должно быть больше 0',
    })
    return
  }

  if (errors.periodName) {
    feedbackStore.showToast({
      type: 'error',
      message: 'Заполните название периода тарифа',
    })
    return
  }

  if (errors.fallbackTariffID) {
    feedbackStore.showToast({
      type: 'error',
      message: 'Выберите тариф автопродления для промо-тарифа',
    })
    return
  }

  const payload: IUpdateTariffPayload = {
    id: tariff.id,
    price: tariff.price,
    periodDays: tariff.periodDays,
    periodName: tariff.periodName,
    isPopular: tariff.isPopular,
    referralBonusDays: tariff.referralBonusDays,
    isPromo: tariff.isPromo,
    fallbackTariffID: tariff.isPromo ? tariff.fallbackTariffID || undefined : undefined,
  }

  const success = await adminStore.updateTariff(payload)
  if (success) {
    // Успешно сохранили — снапшот и ошибки больше не нужны, выходим из edit и сворачиваем панель
    editingTariffSnapshots.delete(tariff.id)
    delete editingTariffErrors[tariff.id]
    editingTariffIds.value.delete(tariff.id)
    expandedTariffIds.value = expandedTariffIds.value.filter((id) => id !== tariff.id)
  }
}

/**
 * Открывает диалог подтверждения удаления тарифа
 *
 * @param {ITariff} tariff - Объект тарифа для удаления (для отображения названия и цены в диалоге)
 */
const deleteTariff = (tariff: ITariff) => {
  tariffToDelete.value = tariff
  isDeleteTariffDialogOpen.value = true
}

/**
 * Подтверждает удаление тарифа через стор и закрывает диалог
 */
const confirmDeleteTariff = async () => {
  if (!tariffToDelete.value) return

  await adminStore.deleteTariff(tariffToDelete.value.id)

  tariffToDelete.value = null
  isDeleteTariffDialogOpen.value = false
}

// Загрузка настроек и тарифов при монтировании компонента
onMounted(async () => {
  await settingsStore.getSettings()
  supportBotUsername.value = settingsStore.supportBotUsername
  showMainLogo.value = settingsStore.showMainLogo
  supportEmail.value = settingsStore.supportEmail
  proxyURLs.value = [...settingsStore.proxyURLs]

  await adminStore.getTariffs()
})
</script>

<template>
  <div class="d-flex flex-column ga-6 pt-4" :class="{ 'pa-4': isTelegram() || mobile }">
    <!-- Навигация -->
    <header>
      <section-header />
    </header>

    <!-- Секция общих настроек -->
    <section class="d-flex flex-column ga-4">

      <v-text-field
        v-model="supportBotUsername"
        label="Username бота поддержки"
        variant="outlined"
        hide-details
        placeholder="your_best_vpn_support"
      />

      <v-text-field
        v-model="supportEmail"
        label="Email поддержки"
        variant="outlined"
        hide-details
        placeholder="support@example.com"
      />

      <v-textarea
        v-model="proxyURLsText"
        label="URL прокси-серверов"
        variant="outlined"
        hide-details
        rows="1"
        auto-grow
        no-resize
        placeholder="Введите URL прокси через запятую"
      />

      <v-checkbox
        v-model="showMainLogo"
        label="Показывать главный логотип"
        color="primary"
        hide-details
      />

      <v-btn
        color="primary"
        variant="flat"
        block
        @click="saveChanges"
      >
        Сохранить
      </v-btn>
    </section>

    <v-divider />

    <!-- Секция управления тарифами -->
    <section class="d-flex flex-column ga-4">
      <div class="text-center text-uppercase">Управление тарифами</div>

      <div v-if="!tariffs || tariffs.length === 0" class="text-center text-text-disabled pa-4">
        Список тарифов пуст
      </div>

      <v-btn
        block
        color="primary"
        variant="outlined"

        prepend-icon="ri-add-line"
        @click="openCreateTariffDialog"
      >
        Добавить тариф
      </v-btn>

      <v-expansion-panels v-model="expandedTariffIds" multiple variant="accordion">
        <v-expansion-panel
          v-for="tariff in tariffs"
          :key="tariff.id"
          :value="tariff.id"
          bg-color="surface"
          rounded="lg"
          class="mb-2"
        >
          <!-- Заголовок карточки тарифа: название периода + цена + чип «Популярный» + иконки действий (карандаш/корзина) -->
          <v-expansion-panel-title hide-actions class="pa-2">
            <div class="d-flex align-center justify-space-between w-100">
              <div class="d-flex align-center ga-2 flex-wrap">
                <span class="text-body-1">{{ tariff.periodName }} — {{ tariff.price }} ₽</span>
                <!-- Чип-метка для популярного тарифа — отображает текущее состояние isPopular -->
                <v-chip
                  v-if="tariff.isPopular"
                  size="x-small"
                  color="warning"
                  variant="flat"
                  class="flex-shrink-0"
                >
                  Популярный
                </v-chip>
              </div>
              <!-- Блок действий: редактирование + удаление, плотной группой справа -->
              <div class="d-flex align-center" :class="{ 'ga-4': !mobile }">
                <v-tooltip text="Редактировать" location="top">
                  <template #activator="{ props }">
                    <v-btn
                      v-bind="props"
                      icon="ri-pencil-line"
                      variant="text"
                      size="small"
                      color="primary"
                      @click.stop="toggleTariffEditMode(tariff)"
                    />
                  </template>
                </v-tooltip>
                <v-tooltip text="Удалить" location="top">
                  <template #activator="{ props }">
                    <v-btn
                      v-bind="props"
                      icon="ri-delete-bin-line"
                      variant="text"
                      size="small"
                      color="error"
                      @click.stop="deleteTariff(tariff)"
                    />
                  </template>
                </v-tooltip>
              </div>
            </div>
          </v-expansion-panel-title>

          <!-- Содержимое карточки — форма с полями; readonly в режиме просмотра, активные в режиме редактирования -->
          <v-expansion-panel-text>
            <v-form @submit.prevent="saveTariffChanges(tariff)">
              <div class="d-flex flex-column ga-2">
                <v-text-field
                  v-model="tariff.id"
                  label="ID"
                  disabled
                  density="compact"
                  variant="outlined"
                  hide-details
                />

                <v-text-field
                  v-model.number="tariff.price"
                  label="Цена"
                  type="number"
                  :readonly="!isTariffEditing(tariff.id)"
                  :error="editingTariffErrors[tariff.id]?.price"
                  density="compact"
                  variant="outlined"
                  hide-details
                />

                <v-text-field
                  v-model.number="tariff.periodDays"
                  label="Период (дни)"
                  type="number"
                  :readonly="!isTariffEditing(tariff.id)"
                  :error="editingTariffErrors[tariff.id]?.periodDays"
                  density="compact"
                  variant="outlined"
                  hide-details
                />

                <v-text-field
                  v-model="tariff.periodName"
                  label="Период (текст)"
                  placeholder="1 месяц"
                  :readonly="!isTariffEditing(tariff.id)"
                  :error="editingTariffErrors[tariff.id]?.periodName"
                  density="compact"
                  variant="outlined"
                  hide-details
                />

                <v-text-field
                  :model-value="tariff.economy"
                  label="Экономия (%)"
                  disabled
                  density="compact"
                  variant="outlined"
                  hide-details
                />

                <v-text-field
                  v-model.number="tariff.referralBonusDays"
                  label="Бонусные дни рефереру"
                  type="number"
                  :readonly="!isTariffEditing(tariff.id)"
                  density="compact"
                  variant="outlined"
                  hide-details
                />

                <v-checkbox
                  v-model="tariff.isPopular"
                  label="Популярный тариф"
                  color="primary"
                  hide-details
                  :readonly="!isTariffEditing(tariff.id)"
                />

                <v-checkbox
                  v-model="tariff.isPromo"
                  label="Промо-тариф"
                  color="primary"
                  hide-details
                  :readonly="!isTariffEditing(tariff.id)"
                />

                <v-select
                  v-if="tariff.isPromo"
                  v-model="tariff.fallbackTariffID"
                  label="Тариф автопродления"
                  :items="regularTariffsOptions"
                  :readonly="!isTariffEditing(tariff.id)"
                  :error="editingTariffErrors[tariff.id]?.fallbackTariffID"
                  density="compact"
                  variant="outlined"
                  hide-details
                />

                <!-- Кнопки сохранения и отмены — только в режиме редактирования, в колонку -->
                <div v-if="isTariffEditing(tariff.id)" class="d-flex flex-column ga-2">
                  <v-btn
                    color="success"
                    variant="flat"
                    type="submit"
                    block
                  >
                    Сохранить
                  </v-btn>
                  <v-btn
                    color="secondary"
                    variant="text"
                    block
                    @click="cancelTariffEdit(tariff)"
                  >
                    Отмена
                  </v-btn>
                </div>
              </div>
            </v-form>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
    </section>

    <!-- Диалог создания тарифа -->
    <v-dialog v-model="isCreateTariffDialogOpen" max-width="600px" :fullscreen="mobile">
      <v-card>
        <v-card-title class="d-flex align-center justify-space-between">
          <div>Добавить новый тариф</div>
          <VIconBtn icon="ri-close-line" @click="isCreateTariffDialogOpen = false" />
        </v-card-title>
        <v-card-text>
          <v-form @submit.prevent="createTariff" class="d-flex flex-column ga-2">
            <v-text-field
              v-model.number="newTariff.price"
              label="Цена"
              type="number"
              :error="createTariffErrors.price"
              variant="outlined"
              hide-details
            />
            <v-text-field
              v-model.number="newTariff.periodDays"
              label="Период (дни)"
              type="number"
              :error="createTariffErrors.periodDays"
              variant="outlined"
              hide-details
            />
            <v-text-field
              v-model="newTariff.periodName"
              label="Период (текст)"
              placeholder="1 месяц"
              :error="createTariffErrors.periodName"
              variant="outlined"
              hide-details
            />
            <v-text-field
              v-model.number="newTariff.referralBonusDays"
              label="Бонусные дни рефереру"
              type="number"
              variant="outlined"
              hide-details
            />
            <v-checkbox
              v-model="newTariff.isPopular"
              label="Популярный тариф"
              color="primary"
              hide-details
            />

            <v-checkbox
              v-model="newTariff.isPromo"
              label="Промо-тариф"
              color="primary"
              hide-details
            />

            <v-select
              v-if="newTariff.isPromo"
              v-model="newTariff.fallbackTariffID"
              label="Тариф автопродления"
              :items="regularTariffsOptions"
              :error="createTariffErrors.fallbackTariffID"
              density="compact"
              variant="outlined"
              hide-details
            />

            <div :class="['d-flex ga-2', mobile ? 'flex-column-reverse' : 'justify-end']">
              <v-btn color="secondary" variant="text" @click="isCreateTariffDialogOpen = false">
                Отмена
              </v-btn>
              <v-btn color="primary" variant="outlined" type="submit">Создать</v-btn>
            </div>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Диалог подтверждения удаления тарифа -->
    <v-dialog v-model="isDeleteTariffDialogOpen" max-width="420">
      <v-card class="pa-5 d-flex flex-column ga-4" rounded="xl">
        <div class="text-h6">Удаление тарифа</div>
        <div>
          Вы уверены, что хотите удалить тариф
          <span v-if="tariffToDelete" class="font-weight-bold">
            {{ tariffToDelete.periodName }} — {{ tariffToDelete.price }} ₽
          </span>?
        </div>
        <div class="d-flex justify-end ga-2">
          <v-btn variant="text" @click="isDeleteTariffDialogOpen = false">Отмена</v-btn>
          <v-btn color="error" variant="flat" @click="confirmDeleteTariff">Удалить</v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
/* Уменьшенный padding внутреннего контейнера раскрытой панели тарифа.
   До __wrapper не добраться utility-классами — приходится переопределять напрямую. */
:deep(.v-expansion-panel-text__wrapper) {
  padding: 8px;
}
</style>
